import { compact, idr } from '@shared/format';

export type Modality = 'text' | 'image' | 'video' | 'embedding';

interface CatalogPricing {
  input_per_1m?: number;
  cached_input_per_1m?: number;
  output_per_1m?: number;
  per_image?: number;
  per_1m_video_tokens?: number;
  per_second_720p?: number;
}

/** GET /v1/models entry. */
export interface CatalogModel {
  id: string;
  slug?: string;
  display_name: string;
  owned_by: string;
  provider?: string;
  family?: string | null;
  modality?: Modality;
  context_window: number;
  max_output_tokens: number;
  capabilities: {
    chat?: boolean;
    stream: boolean;
    tools: boolean;
    vision: boolean;
    reasoning: boolean;
    json_schema?: boolean;
    sampling_params?: boolean;
  };
  pricing: CatalogPricing | null;
  status?: string;
}

export const modalityOf = (m: Pick<CatalogModel, 'modality'>): Modality => m.modality ?? 'text';

export const providerOf = (m: Pick<CatalogModel, 'provider' | 'owned_by'>): string => m.provider ?? m.owned_by;

export const MODALITY_LABEL: Record<Modality, string> = { text: 'Teks', image: 'Gambar', video: 'Video', embedding: 'Embedding' };

export function playgroundHref(m: CatalogModel): string {
  const mode = modalityOf(m);
  // no playground mode
  if (mode === 'embedding') return '/docs/embeddings';
  return `/playground?model=${encodeURIComponent(m.id)}${mode === 'text' ? '' : `&mode=${mode}`}`;
}

export function priceShort(m: CatalogModel): string {
  const p = m.pricing;
  if (!p) return 'Harga belum tersedia';
  const mode = modalityOf(m);
  if (mode === 'image') return p.per_image !== undefined ? `${idr(p.per_image)}/gambar` : '—';
  if (mode === 'video') return p.per_second_720p !== undefined ? `${idr(p.per_second_720p)}/detik` : '—';
  if (mode === 'embedding') return `${idr(p.input_per_1m ?? 0)} input`;
  return `${idr(p.input_per_1m ?? 0)} input · ${idr(p.output_per_1m ?? 0)} output`;
}

/** Comparable price for sorting. */
export function unitPrice(m: CatalogModel): number {
  const p = m.pricing;
  if (!p) return Number.POSITIVE_INFINITY;
  const mode = modalityOf(m);
  if (mode === 'image') return p.per_image ?? Number.POSITIVE_INFINITY;
  if (mode === 'video') return p.per_second_720p ?? Number.POSITIVE_INFINITY;
  return (p.input_per_1m ?? 0) + (p.output_per_1m ?? 0);
}

export function contextLabel(m: CatalogModel): string {
  return compact(m.context_window).replace('.0', '');
}

/** Picks a varied showcase. */
export function popularModels(models: CatalogModel[], count = 6): CatalogModel[] {
  const wanted = ['claude-sonnet-5-5', 'gpt-5.5', 'gemini-3.8-flash', 'seedream-5.0-flash', 'deepseek-v4-pro', 'seedance-2.0-fast'];
  const picked: CatalogModel[] = [];
  for (const slug of wanted) {
    const hit = models.find((m) => m.id === slug);
    if (hit) picked.push(hit);
  }
  // one per family
  for (const m of models) {
    if (picked.length >= count) break;
    if (picked.some((p) => p.id === m.id || (p.family && p.family === m.family))) continue;
    picked.push(m);
  }
  return picked.slice(0, count);
}
